import { ReactNode, useEffect, useMemo } from 'react';

import { GetLocationsResponseDTO, MODULE } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { ColumnFiltersState, RowSelectionState } from '@tanstack/react-table';

import locationVector from '@/assets/vectors/location.svg';
import manholeShrugVector from '@/assets/vectors/manhole-shrug.svg';
import UndoIcon from '@/components/icons/undo';
import { TablePageTable } from '@/components/table-page/table-page-table';
import { usePermissions } from '@/hooks/use-permissions';
import { useModulesStore } from '@/stores/modules.store';
import { useWorkspaceNavigate } from '@/utils/routes';

import { createLocationsTableColumns } from './table-meta';

export function LocationsTable({
  notFoundDisplay,
  data,
  loading,
  onRowSelectionChange,
  rowSelection,
  columnFilters,
  onColumnFiltersChange,
  sorting,
  grouping,
  columnOrder,
  columnVisibility,
  onSortingChange,
}: {
  notFoundDisplay: ReactNode;
  data: GetLocationsResponseDTO['data'];
  loading: boolean;
  onRowSelectionChange: (selectedRows: RowSelectionState) => void;
  rowSelection: RowSelectionState;
  columnFilters: ColumnFiltersState;
  onColumnFiltersChange: (columnFilters: ColumnFiltersState) => void;
  sorting: { key?: string; direction?: 'asc' | 'desc' };
  grouping: string[];
  columnOrder: Array<{ id: string; active: boolean }>;
  columnVisibility: Record<string, boolean>;
  onSortingChange: (sorting: { key?: string; direction?: 'asc' | 'desc' }) => void;
}) {
  const navigate = useWorkspaceNavigate();
  const { syncModuleColumns } = useModulesStore();
  const { canManageLocations } = usePermissions();
  const columns = createLocationsTableColumns({ canManageLocations });

  const initialSorting = useMemo(
    () =>
      sorting.key
        ? [
            {
              id: sorting.key,
              desc: sorting.direction === 'desc',
            },
          ]
        : [],
    [sorting.key, sorting.direction],
  );

  useEffect(() => {
    syncModuleColumns(MODULE.Locations, createLocationsTableColumns());
  }, [syncModuleColumns]);

  return (
    <div className="flex-1 bg-white">
      <TablePageTable<GetLocationsResponseDTO['data'][number]>
        data={data}
        columns={columns}
        notFoundDisplay={notFoundDisplay}
        loading={loading}
        onSortingChange={onSortingChange}
        initialSorting={initialSorting}
        grouping={grouping}
        columnOrder={(columnOrder || []).map((column) => column.id)}
        onRowClick={(row) => {
          // /locations/:id/overview is the editor — Members can't edit.
          if (!canManageLocations) return;
          navigate(`/locations/${row.id}/overview`);
        }}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        columnVisibility={columnVisibility}
        columnFilters={columnFilters}
        onColumnFiltersChange={onColumnFiltersChange}
      />
    </div>
  );
}

export function NoLocationsCreatedDisplay({ openLocationDrawer }: { openLocationDrawer: () => void }) {
  const { canManageLocations } = usePermissions();
  return (
    <div className="absolute inset-0 m-auto flex w-[383px] flex-col items-center justify-center gap-[20px]">
      <img src={locationVector} alt="Location" className="h-[150px] w-[150px]" />
      <span className="text-xl leading-[30px] font-medium">No locations have been created yet</span>
      {canManageLocations && (
        <>
          <div className="flex flex-col gap-0 text-center">
            <span className="text-secondary-text text-sm">
              Keep track of where your assets are by adding the addresses of your sites, projects and facilities.
            </span>
          </div>
          <div className="pointer-events-auto flex gap-1">
            <Button variant="success" onClick={openLocationDrawer}>
              Create Location
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export function NoLocationsFoundFromSearchDisplay() {
  return (
    <div className="absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-4 p-4">
      <img src={manholeShrugVector} alt="Manhole shrug" className="h-28" />
      <span className="text-sm">No locations match your search</span>
    </div>
  );
}

export function NoLocationsFoundFromFiltersDisplay({
  openLocationDrawer,
  removeColumnFilters,
}: {
  openLocationDrawer: () => void;
  removeColumnFilters: () => void;
}) {
  const { canManageLocations } = usePermissions();
  return (
    <div className="bg-grey-1 absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-4 p-4">
      <img src={manholeShrugVector} alt="Manhole shrug" className="h-28" />
      <span className="text-xl font-medium">No locations match your filters</span>
      <span className="text-secondary-text text-sm">Try adjusting your column filters{canManageLocations ? ' or create a new location' : ''}.</span>
      <div className="pointer-events-auto flex gap-2">
        <Button variant="grey" onClick={removeColumnFilters} className="gap-1 font-normal">
          <UndoIcon />
          Remove all filters
        </Button>
        {canManageLocations && (
          <Button variant="success" onClick={openLocationDrawer}>
            Create Location
          </Button>
        )}
      </div>
    </div>
  );
}
